import type { ConnectError } from '@connectrpc/connect'
import {
  PlantMasterErrorReason,
  PlantMasterErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import { plantAreaChangeBlockedMessage } from '~/models/areaChange'

/**
 * `PlantMasterErrorReason` (occto-plant-business-master-reason-registry
 * 設計書、backend `occtomaster` domain) → i18n key + field routing registry
 * for the PlantMaster Create/Update modals (BgMaster/SubmitterMaster と同型)。
 *
 * `MEMBER_BUSINESS_MASTER_NOT_FOUND` has no `field` (Design Note D3:
 * members is a list and the missing member's index does not surface on
 * the wire, so a field-scoped mapping would misattribute the error when a
 * non-representative member is the one missing) — it resolves to the
 * top-level alert (operation-scoped).
 */
const PLANT_MASTER_REASON_KEYS: Record<
  PlantMasterErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [PlantMasterErrorReason.UNSPECIFIED]: undefined,
  [PlantMasterErrorReason.PLANT_CODE_INVALID_FORMAT]: {
    key: 'page:adminOcctoMaster.serverError.reason.plantCodeInvalidFormat',
    field: 'plantCode',
  },
  [PlantMasterErrorReason.MEMBER_BUSINESS_MASTER_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.serverError.reason.businessMasterNotFound',
  },
  // エリア変更の歯止めは文言が登録先の一覧を含む (plantAreaChangeBlockedMessage)。
  // 確認が要る方は画面が確認ダイアログで受けるので、ここでは文言にしない。
  [PlantMasterErrorReason.AREA_CHANGE_BLOCKED_BY_REGISTRATION]: undefined,
  [PlantMasterErrorReason.AREA_CHANGE_NEEDS_CONFIRMATION]: undefined,
}

/**
 * `resolveDetailedError` for PlantMaster's `useCrudModalForm` (Create /
 * Update). Resolves the `google.rpc.ErrorInfo` Connect error detail, if
 * any, against `PLANT_MASTER_REASON_KEYS` via the
 * `PlantMasterErrorReasonSchema` reflection lookup.
 */
export function resolvePlantMasterDetailedError(
  err: ConnectError,
  t: TFunction,
): AppErrorInfoResolution | undefined {
  const blocked = plantAreaChangeBlockedMessage(t, err)
  if (blocked !== undefined) {
    return { kind: 'operation', message: blocked }
  }
  return resolveAppErrorInfoMessage(
    err,
    PlantMasterErrorReasonSchema,
    PLANT_MASTER_REASON_KEYS,
    t,
  )
}
